import { Space, SpaceProps } from 'antd';
import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { Button, LinkButton } from 'components/atoms/button';
import { ClassNameOnlyProps } from 'components/ozone-component';
import { BodyShort01, ProductiveHeading05 } from 'components/styles';

export const Image = styled.div<Pick<SpaceProps, 'size'>>`
  ${ifProp(
    { size: 'large' },
    css`
      width: 422px;
      height: 264px;
    `,
    ifProp(
      { size: 'middle' },
      css`
        width: 80px;
        height: 80px;
      `,
    ),
  )};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.div`
  ${ProductiveHeading05};
`;

const Message = styled.div`
  ${BodyShort01};
  width: 80%;
`;

const PrimaryCTA = styled(Button).attrs({
  type: 'primary',
  iconPlacement: 'right',
})``;

const SecondaryCTA = styled(LinkButton)`
  display: block;
  text-align: left;
`;

export const Content = styled(Space)``;
const Info = Content;
const CTA = Content;

export const Container = styled(Space)`
  width: 424px;
  max-width: 424px;

  ${Title}, ${Message} {
    width: 80%;
  }

  ${ifProp(
    { direction: 'horizontal' },
    css`
      ${Content} {
        width: 100%;
        max-width: 100%;
      }

      ${ifProp(
        { size: 'large' },
        css`
          ${Content} {
            width: 288px;
          }
        `,
      )};
    `,
  )};
`;

const StyledCallToAction = styled(Space).attrs({
  direction: 'vertical',
  size: 16,
})``;

type CallToAction = typeof StyledCallToAction & {
  Primary: typeof PrimaryCTA;
  Secondary: typeof SecondaryCTA;
};

const CallToAction: CallToAction = StyledCallToAction as any;

CallToAction.Primary = PrimaryCTA;
CallToAction.Secondary = SecondaryCTA;

interface EmptyStateComponentProps extends ClassNameOnlyProps, SpaceProps {
  image?: React.ReactNode;
  title?: React.ReactNode;
  message?: React.ReactNode;
  primaryCTA?: React.ReactNode;
  secondaryCTA?: React.ReactNode;
}

const EmptyStateComponent: FC<EmptyStateComponentProps> = (props) => {
  const {
    className,
    image,
    title,
    message,
    primaryCTA,
    secondaryCTA,
    direction = 'vertical',
    size = 'middle',
    children,
    ...rest
  } = props;

  return (
    <Container
      className={className}
      direction={direction}
      size={size}
      {...rest}
    >
      {image && <Image size={size}>{image}</Image>}
      <Content
        direction="vertical"
        size={size === 'middle' && direction === 'horizontal' ? 8 : 24}
      >
        {(title || message) && (
          <Info direction="vertical">
            {title && <Title>{title}</Title>}
            {message && <Message>{message}</Message>}
          </Info>
        )}
        {(primaryCTA || secondaryCTA) && (
          <CTA direction="vertical" size={16}>
            {primaryCTA}
            {secondaryCTA}
          </CTA>
        )}
      </Content>
      {children}
    </Container>
  );
};

type EmptyState = typeof EmptyStateComponent & {
  Image: typeof Image;
  Title: typeof Title;
  Message: typeof Message;
  CallToAction: typeof CallToAction;
};

const EmptyState: EmptyState = EmptyStateComponent as any;

EmptyState.Image = Image;
EmptyState.Title = Title;
EmptyState.Message = Message;
EmptyState.CallToAction = CallToAction;

const EmptyStateContainer = styled.div<{ $transparent?: boolean }>`
  ${ifProp(
    '$transparent',
    css`
      background-color: transparent;
    `,
    css`
      background-color: var(--white-100);
    `,
  )};

  padding: 160px 128px;
`;

export { EmptyState, EmptyStateContainer };
